import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import clsx from "clsx";
import { Controller, type FieldPath, type FieldValues } from "react-hook-form";

import type { BaseInputProps, InputOption } from "@/shared/types";
import { WrapperInput } from "./WrapperInput";

type RadioInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number | boolean = string,
> = BaseInputProps<TFieldValues, TName> & {
  options: InputOption<TValue>[];
  direction?: "row" | "column";
};

export function RadioInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number | boolean = string,
>({
  control,
  name,
  label,
  description,
  disabled,
  required,
  className,
  options,
  direction = "row",
}: RadioInputProps<TFieldValues, TName, TValue>) {
  const inputClasses = getThemeComponentGroup("input");
  const formSectionClasses = getThemeComponentGroup("formSection");

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <WrapperInput
          label={label}
          error={fieldState.error?.message}
          description={description}
          required={required}
          className={className}
        >
          <div
            className={clsx("flex", direction === "column" ? "flex-row" : "flex-wrap items-center")}
          >
            {options.map((option) => {
              const checked = field.value === option.value;

              return (
                <label
                  key={String(option.value)}
                  className={clsx(
                    "inline-flex min-h-10 cursor-pointer items-center gap-1 py-2 px-1 text-[11px] transition",
                    checked
                      ? clsx(
                          getThemeClass(inputClasses.focusBorderColor, ""),
                          getThemeClass(inputClasses.focusRingColor, ""),
                          getThemeClass(inputClasses.focusBorderColor, "text-theme-primary")
                        )
                      : clsx(
                          getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
                          getThemeClass(inputClasses.bgColor, ""),
                          getThemeClass(
                            inputClasses.textColor || formSectionClasses.helperTextColor,
                            "text-theme-text"
                          )
                        ),
                    disabled && "cursor-not-allowed opacity-60"
                  )}
                >
                  <input
                    type="radio"
                    className={clsx(
                      "h-4 w-4",
                      getThemeClass(inputClasses.focusBorderColor, "accent-theme-primary")
                    )}
                    checked={checked}
                    disabled={disabled || option.disabled}
                    onChange={() => field.onChange(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </div>
        </WrapperInput>
      )}
    />
  );
}
